import { explanationDraftSchema } from '@mio/shared';
import { z } from 'zod';

import type { PendingExplanation } from './explanation-draft';

const schema = z
  .object({
    version: z.literal(1),
    baseline: explanationDraftSchema,
    text: z.string().max(8000),
    pendingRevision: z.number().int().nonnegative().optional(),
  })
  .strict()
  .refine(
    (value) =>
      value.pendingRevision === undefined || value.pendingRevision === value.baseline.revision,
  );

// A response from an unmounted controller must not erase a newer controller's text.
const writers = new Map<string, symbol>();

/** Tab-scoped recovery survives reloads without overwriting another tab's work. */
export function pendingExplanation(ownerId: string | null, attemptId: string | null) {
  const key =
    ownerId && attemptId
      ? `mio.workshop.explanation.v1:${encodeURIComponent(ownerId)}:${encodeURIComponent(attemptId)}`
      : null;
  const write = (value: PendingExplanation | null) => {
    if (!key) return;
    try {
      if (value) sessionStorage.setItem(key, JSON.stringify({ version: 1, ...value }));
      else sessionStorage.removeItem(key);
    } catch {
      // Local recovery is best effort; it is never labelled a confirmed server save.
    }
  };
  return {
    read(): PendingExplanation | null {
      if (!key) return null;
      try {
        const value = sessionStorage.getItem(key);
        if (!value || value.length > 100_000) return null;
        const parsed = schema.safeParse(JSON.parse(value));
        if (!parsed.success) return null;
        return {
          baseline: parsed.data.baseline,
          text: parsed.data.text,
          ...(parsed.data.pendingRevision !== undefined
            ? { pendingRevision: parsed.data.pendingRevision }
            : {}),
        };
      } catch {
        // Storage may be unavailable. Server saves and unload protection still work.
        return null;
      }
    },
    write,
    writer() {
      const token = Symbol();
      let active = false;
      const current = () => active && (!key || writers.get(key) === token);
      return {
        current,
        activate() {
          active = true;
          if (key) writers.set(key, token);
        },
        release() {
          active = false;
          if (key && writers.get(key) === token) writers.delete(key);
        },
        write(value: PendingExplanation | null) {
          if (current()) write(value);
        },
      };
    },
  };
}
